import React, { FC, useMemo } from 'react';
import { Select as AntdSelect, SelectProps as AntdSelectProps, SelectProps } from 'antd';
import styled from 'styled-components';
import { DirectionProp } from '~/types/Direction';
import { useDirection } from '~/hooks/i18nHooks';
import direction from '~/utils/direction';
import colors from '~/constants/colors';

function Select<VT = any[]>(props: AntdSelectProps<VT>) {
  const { options, id, showSearch, loading, value: rawInputValue, children, ...rest } = props;
  const value = Array.isArray(rawInputValue)
    ? rawInputValue
    : typeof rawInputValue === 'object'
      ? undefined
      : rawInputValue;

  const dir = useDirection();

  const resultOptions = useMemo(() => {
    return options?.map((opt) => ({
      ...opt,
      value: opt.value,
      label: reduceSpaces(opt.label as string),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  return (
    <StyledSelect<any>
      id={loading ? '' : id}
      showSearch={showSearch}
      filterOption={
        showSearch
          ? (enteredValue: string, option: any) => {
            return (
              option.label?.toLowerCase().indexOf(reduceSpaces(enteredValue)?.toLowerCase()) !==
              -1
            );
          }
          : undefined
      }
      loading={loading}
      dir={dir}
      value={value}
      {...rest}
    >
      {resultOptions?.map((option) => {
        // eslint-disable-next-line
        // @ts-ignore
        const { label, key, value: optionValue, ...restOption } = option;
        return (
          <AntdSelect.Option
            key={key || optionValue}
            value={optionValue}
            label={label}
            dir={dir}
            {...restOption}
            id={`${id}_${optionValue}`}
          >
            {React.isValidElement(label) ? label : label?.toString()}
          </AntdSelect.Option>
        );
      })}
      {children}
    </StyledSelect>
  );
}

const reduceSpaces = (value: string | undefined) => {
  if (!value) return value;
  return value.split(' ').filter(Boolean).join(' ');
};

type UIProps = SelectProps<any> & DirectionProp;

const StyledSelect = styled<FC<UIProps>>(AntdSelect)`
  &.ant-select-single {
    .ant-select-selection-item {
      ${direction.padding.right('18px !important')};
      ${direction.padding.left('0px !important')};
    }
  }

  &.ant-select-multiple {
    .ant-select-selection-item {
      background-color: #f5f5f5;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      margin-inline-end: 4px;
      padding-inline-start: 8px !important;
      padding-inline-end: 4px !important;
    }
    
    .ant-select-selection-item-content {
      margin-inline-end: 4px;
    }

    .ant-select-selection-item-remove {
      color: ${colors.grey[500]};
      &:hover {
        color: ${colors.errorColor};
      }
    }
  }

  .ant-select-selection-search {
    ${direction.left('15px !important')};
    ${direction.right('unset !important')};
  }
  
  .ant-select-arrow,
  .ant-select-clear {
    ${direction.right(11)};
    ${direction.left('unset')};
  }
`;

export default Select;
